import { useGesture } from '@use-gesture/react';
import clsx from 'clsx';
import type { ScaleBand } from 'd3-scale';
import { useRef } from 'react';

import { axisContainerStyle } from './AxisArea.sty.js';
import { XTickWithTruncation } from './XTickWithTruncation.js';
import { XYChartPanningControls } from './XYChartPanningControls.js';
import { AxisTop } from '../../../core/components/axes/AxisTop.js';
import { DivAxisWrapper } from '../../../core/components/axes/components/DivAxisWrapper.js';
import { axisFontProps } from '../../../core/components/axes/constants.js';
import { getXTickMaxSize } from '../../../core/components/axes/utils/calculate-axis-size.js';
import {
  getTickLabelPropsForCategoryAxis,
  getTickLabelPropsForValueAxis,
} from '../../../core/components/axes/utils/get-tick-label-props.js';
import { isTickLabelSpaceAvailable } from '../../../core/components/axes/utils/is-tick-label-visible.js';
import { ChartGrid } from '../../../core/components/chart-grid/ChartGridRoot.js';
import { KEYBOARD_HANDLERS } from '../../../core/components/zoom/constants.js';
import { cursorStyle } from '../../../core/components/zoom/ZoomCursors.sty.js';
import { useAxesTicks } from '../../hooks/useAxesTicks.js';
import { useVisibleAxesConfig } from '../../hooks/useVisibleAxesConfig.js';
import { useVisibleAxesScales } from '../../hooks/useVisibleAxesScales.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import { useZoomState } from '../../store/selectors.js';
import { isCategorical } from '../../types/guards.js';
import { AREAS } from '../../utils/grid-templates.js';
import { useApplyPanDelta } from '../overlay/hooks/useApplyPanDelta.js';

export function XYChartTopAxisArea() {
  const axisRef = useRef<HTMLDivElement>(null);

  const { topTicks } = useAxesTicks();
  const { top: topConfig } = useVisibleAxesConfig();
  const { top: topScale } = useVisibleAxesScales();
  const { zoomOutEnabled } = useZoomState();
  const {
    axesMetadata: { isPanEnabled },
  } = useXYChartData();

  const canPan = isPanEnabled && zoomOutEnabled;

  const tickFormat = (_: unknown, index: number) =>
    topTicks.formattedTicks[index];

  const isCategoricalAxis = topConfig?.type
    ? isCategorical(topConfig.type)
    : false;
  const tickLabelProps = isCategoricalAxis
    ? getTickLabelPropsForCategoryAxis('top', topConfig?.tickLabelLayout)
    : getTickLabelPropsForValueAxis('top', topConfig?.tickLabelLayout);

  const areCategoricalTicksNotFittable =
    topScale &&
    isCategoricalAxis &&
    !isTickLabelSpaceAvailable((topScale as ScaleBand<string>).step());

  const hideAxis =
    topConfig === undefined ||
    areCategoricalTicksNotFittable ||
    topScale === undefined;

  const isTickRotated = topConfig?.tickLabelLayout === 'vertical';

  const handlePan = useApplyPanDelta();

  useGesture(
    {
      onDrag: (e) =>
        KEYBOARD_HANDLERS.includes(e.type) ? null : handlePan(-e.delta[0]),
    },
    {
      target: axisRef,
      eventOptions: { passive: false },
      drag: { filterTaps: true, pointer: { buttons: [1, 4] } },
      enabled: canPan,
    },
  );

  return hideAxis ? null : (
    <ChartGrid.GridArea gridArea={AREAS.topAxis}>
      {({ availableWidth, availableHeight }) => (
        <div
          className={clsx(
            cursorStyle({ isPanEnabled: canPan }),
            axisContainerStyle,
          )}
          ref={axisRef}
        >
          {canPan && (
            <XYChartPanningControls onClickPan={(delta) => handlePan(-delta)} />
          )}
          <DivAxisWrapper
            divAxisStyle={axisFontProps}
            ticks={topTicks.formattedTicks}
            label={topConfig.label}
            isTickVertical
            isTickRotated={isTickRotated}
          >
            <AxisTop
              data-testid="axis-top"
              scale={topScale}
              containerSize={availableWidth}
              height={availableHeight}
              tickValues={topTicks.ticks}
              tickLabelProps={tickLabelProps}
              tickFormat={tickFormat}
              label={topConfig.label}
              tickComponent={(renderProps) => (
                <XTickWithTruncation
                  renderProps={renderProps}
                  maxSpaceForTruncation={getXTickMaxSize(
                    availableHeight,
                    availableWidth,
                    isTickRotated,
                    topScale,
                    topConfig.label,
                  )}
                  centerTick={isCategoricalAxis}
                  isTickRotated={isTickRotated}
                  position="top"
                />
              )}
            />
          </DivAxisWrapper>
        </div>
      )}
    </ChartGrid.GridArea>
  );
}

XYChartTopAxisArea['displayName'] = 'XYChartTopAxisArea';
